import _ from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PermissionService } from "src/services/permissionService/permissionService";
import { RootState, useDispatch } from "src/store";
import { SecondaryButton } from "src/ui/shared/CustomButton";
import { showErrorToast } from "src/ui/shared/toasts/Toasts";

import { useCurrentWorkspace } from "@dashboard/workspaces/api/helpers";
import { fetchProductChangelog } from "@products/store";
import { selectBuilds } from "@products/store/selectors/builds";
import { selectProductChangelogsById } from "@products/store/selectors/products";
import { Product } from "@products/types";

import { ChangesDialog } from "./ChangesDialog";

type Props = { product: Product | null | undefined };

export const ChangeSection = (props: Props) => {
    const [isChangesOpen, setIsChangesOpen] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(false);
    const dispatch = useDispatch();

    const { data: workspace } = useCurrentWorkspace();
    const { t } = useTranslation(["pages"]);
    const { productAbility } = PermissionService();
    const cannotManageProducts = productAbility(workspace).cannot("manage", "Product");

    const productChanges = useSelector((state: RootState) => selectProductChangelogsById(state, props.product?.id));
    const builds = useSelector(selectBuilds)?.filter((build) => build.productId === props.product?.id);
    const buildsVersions = _.uniq(builds?.map((build) => build.version));
    const versionsObject = _.zipObject(
        buildsVersions,
        buildsVersions.map(() => []),
    );

    React.useEffect(() => {
        const fetchProductChanges = async () => {
            if (props.product?.id) {
                setIsLoading(true);
                try {
                    await dispatch(fetchProductChangelog({ id: props.product.id })).unwrap();
                } catch (error) {
                    const err = error as Error;
                    showErrorToast(err.message || "Something went wrong while fetching files");
                } finally {
                    setIsLoading(false);
                }
            }
        };

        void fetchProductChanges();
    }, [dispatch, props.product?.id]);

    const isProductMissing = !props.product;
    const changesTooltip = isProductMissing
        ? t("pages:productPage.tooltip.noProductSelected")
        : t("pages:productPage.tooltip.changesButton");

    return (
        <React.Fragment>
            <SecondaryButton
                data-testid="changes-button"
                onClick={() => setIsChangesOpen(true)}
                size="small"
                disabled={isLoading || cannotManageProducts || isProductMissing}
                tooltipProps={{
                    title: changesTooltip,
                    hide: false,
                }}
            >
                <i className="fa-solid fa-list-ul" style={{ fontSize: 18 }} />
            </SecondaryButton>
            <ChangesDialog
                open={isChangesOpen}
                onClose={() => setIsChangesOpen(false)}
                title="Product releases"
                changes={{ ...versionsObject, ...productChanges }}
                productId={props.product?.id}
            />
        </React.Fragment>
    );
};
